import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCRM } from '../contexts/CRMContext';

const { FiDollarSign, FiCalendar, FiUser } = FiIcons;

const Deals = () => {
  const { deals } = useCRM();

  const dealStages = [
    { name: 'Prospecting', deals: deals.filter(d => d.stage === 'prospecting') },
    { name: 'Qualification', deals: deals.filter(d => d.stage === 'qualification') },
    { name: 'Proposal', deals: deals.filter(d => d.stage === 'proposal') },
    { name: 'Negotiation', deals: deals.filter(d => d.stage === 'negotiation') },
    { name: 'Closed Won', deals: deals.filter(d => d.stage === 'closed_won') },
  ];

  const getStageColor = (stage) => {
    switch (stage) {
      case 'prospecting': return 'bg-blue-100 border-blue-200';
      case 'qualification': return 'bg-yellow-100 border-yellow-200';
      case 'proposal': return 'bg-orange-100 border-orange-200';
      case 'negotiation': return 'bg-purple-100 border-purple-200';
      case 'closed_won': return 'bg-green-100 border-green-200';
      default: return 'bg-gray-100 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Deals Pipeline</h1>
        <p className="text-gray-600">Track deals through your sales pipeline</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {dealStages.map((stage, index) => (
          <motion.div
            key={stage.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-lg border-2 ${getStageColor(stage.name.toLowerCase().replace(' ', '_'))}`}
          >
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">{stage.name}</h3>
              <p className="text-sm text-gray-500">{stage.deals.length} deals</p>
            </div>
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              {stage.deals.map((deal) => (
                <motion.div
                  key={deal.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-pointer"
                >
                  <div className="font-medium text-gray-900 mb-2">{deal.title}</div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiUser} className="h-4 w-4 mr-2" />
                      {deal.company}
                    </div>
                    <div className="flex items-center text-sm text-green-600">
                      <SafeIcon icon={FiDollarSign} className="h-4 w-4 mr-2" />
                      ${deal.value?.toLocaleString()}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                      {deal.close_date}
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${deal.probability || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">{deal.probability || 0}%</span>
                  </div>
                </motion.div>
              ))}
              {stage.deals.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No deals in this stage</p>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Deals;